import type { ReactNode } from "react";

import type { EventCardTarget } from "components/EventSummaryCard";
import { cn } from "utils";

import {
  EventCardShell,
  EventMetaLine,
  eventInitial,
  eventTileFill,
  type EventCardEvent,
} from "./EventTileParts";

/**
 * One event of the browse grid: a picture (or a Dusk gradient block with the
 * event's initial) on top, then title, dates and place.
 *
 * The picture band has a fixed aspect ratio, so the grid keeps its shape
 * while images load. Its image is decoration, since the title below names the
 * event.
 */
export function EventTile({
  event,
  target,
  today,
  badge,
  testId = "event-card",
}: {
  event: EventCardEvent;
  target?: EventCardTarget | null;
  /** Today's `yyyy-mm-dd`, which the dates are written against. */
  today: string;
  /** A marker on the picture band, such as Registered. */
  badge?: ReactNode;
  testId?: string;
}) {
  return (
    <EventCardShell
      target={target}
      testId={testId}
      variant="tile"
      className="group relative flex h-full min-w-0 flex-col overflow-hidden rounded-[1.375rem] border border-border bg-card text-card-foreground wrap-anywhere transition-shadow hover:shadow-card-hover"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-3.5 pb-4 pt-3">
        <h3 className="font-display text-[1.1875rem] font-normal leading-[1.15] text-card-foreground">
          {event.title}
        </h3>
        <EventMetaLine
          event={event}
          today={today}
          className="text-[0.8125rem] leading-snug text-muted-foreground"
        />
      </div>
      {/* After the text in the DOM, so the title is read first and not the
          initial; first on screen through `order`. */}
      <div
        data-testid="event-tile-band"
        className={cn(
          "relative order-first aspect-[16/10] w-full shrink-0 overflow-hidden",
          !event.image && eventTileFill(event.title),
        )}
      >
        {event.image ? (
          <img
            src={event.image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <span
            aria-hidden="true"
            data-testid="event-tile-initial"
            className="absolute inset-0 flex items-center justify-center font-display text-8xl leading-none text-white/20"
          >
            {eventInitial(event.title)}
          </span>
        )}
      </div>
      {/* After the title in the DOM, so the event is read before its marker,
          and drawn over the band by position. */}
      {badge && <div className="absolute left-2 top-2">{badge}</div>}
    </EventCardShell>
  );
}
